/**
 * @file FontField.tsx
 * @description Общее поле «Шрифт» элемента конструктора (phase19.16, партия
 * SB120 «свобода выбора», этап 0; site-builder.md SB120 «шрифт — любой из
 * каталога и своих», SB10 роли шрифтов; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §4).
 *
 * ```
 * Шрифт                        ↺ как у сайта
 * [ По роли сайта | Свой шрифт ]
 *  [Аа Заголовки][Аа Основной][Аа Акцент]     ← «По роли»
 *  Montserrat · общий для сайта · Сменить →
 *  ┌ Аа Lora · с засечками · 400, 700  Сменить ┐ ← «Свой»
 * Начертание / Регистр / Размер ↺ 32 px (H2) / Разрядка ↺ 0 em
 * ! На странице будет 4 шрифта — загрузка медленнее. Лучше до 3.
 * ```
 *
 * Значение — `ElementFont` из shared (`element-font.ts`) и необязательные
 * `weight / case / size / tracking` (нет — «как у роли»). Все изменения —
 * одним объектом `onChange(patch)`: ключ со значением `undefined` = сбросить.
 * Режим следует из значения; «Свой шрифт» открывает каталог
 * (`FontCatalogDialog`, на 360 во весь экран) с «Шрифтами сайта» и
 * «Недавними» сверху, запись — по «Выбрать», выбор запоминается в «Недавних».
 *
 * Не запреты, а подсказки (SB120): больше 3 семейств на странице
 * (`familiesOnPage` — посчитано вызывающим с учётом выбранного,
 * `countFontFamilies`), мелкий H1. Сбои: список своих шрифтов не загрузился —
 * «Шрифты сайта доступны. [Повторить]»; свой шрифт удалён — причина и ✕ в
 * сводке группы через `onStatus`.
 *
 * Части: `font/FontModeSwitch`, `font/FontRoleChips`, `font/FontCustomCard`,
 * `font/FontTypeFields`, `font/FontQuickPicks`; правила —
 * `lib/site-fields/font-field-model`.
 */

import { DEFAULT_ELEMENT_FONT, elementFontKey, type ElementFont, type ElementFontStyle, type FontRole, type SiteFontRegistry, type SiteTheme } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { NoticeStrip } from "../ui/NoticeStrip";
import { FontCatalogDialog } from "../site-view/appearance/FontCatalogDialog";
import { useShopFonts } from "../site-view/shop-fonts-context";
import { FontCustomCard } from "./font/FontCustomCard";
import { FontModeSwitch, type FontMode } from "./font/FontModeSwitch";
import { FontQuickPicks } from "./font/FontQuickPicks";
import { FontRoleChips } from "./font/FontRoleChips";
import { FontTypeFields, type FontTypeInherit } from "./font/FontTypeFields";
import type { NumberPreset } from "./NumberPresets";
import { useFieldStatus } from "../../hooks/site-fields/useFieldStatus";
import { useRecentFonts } from "../../hooks/site-fields/useRecentFonts";
import {
  customFontState,
  familiesWarning,
  fontFieldStatus,
  type FontFieldPatch,
  type FontFieldStatus,
} from "../../lib/site-fields/font-field-model";

export type { FontFieldPatch, FontFieldStatus } from "../../lib/site-fields/font-field-model";

export interface FontFieldProps extends ElementFontStyle {
  /** Подпись поля; по умолчанию «Шрифт». */
  label?: string;
  value: ElementFont;
  onChange: (patch: FontFieldPatch) => void;
  /** Живой предпросмотр размера и разрядки, пока тянут ползунок (раз в кадр). */
  onPreview?: (patch: FontFieldPatch) => void;
  /** Тема черновика: шрифты ролей и `eyebrowFrom`. */
  theme: SiteTheme;
  /** Реестр шрифтов магазина (`useShopFonts().registry`): начертания, названия, удалён ли свой. */
  registry: SiteFontRegistry;
  /** Роль, которой элемент пишется «как у сайта» (первая часть `elementFontParts`). */
  autoRole?: FontRole;
  /** Размер и разрядка «как у роли/уровня»: `{ sizePx: 32, label: "H2" }`. */
  inherit: FontTypeInherit;
  /** Ступени размера по уровню: [24 H3][32 H2][48 H1]. */
  sizePresets?: readonly NumberPreset[];
  /** Мягкая подсказка о мелком размере (`H1_SIZE_HINT`). */
  sizeHint?: { below: number; text: string };
  /** Под ползунком размера — строка «на телефоне N px · Своё ›» (SB120 этап 3а). */
  sizeExtra?: ReactNode;
  /** Сколько семейств будет на странице с учётом выбранного; > 3 — предупреждение. */
  familiesOnPage?: number;
  /** «Сменить →» у роли — во «Оформление → Шрифты» с раскрытой ролью. */
  onOpenSiteFonts?: (role: FontRole) => void;
  /** Курсор или фокус над полем — подсветка элемента на канве. */
  onHover?: (inside: boolean) => void;
  /** Состояние для заголовка группы: ✕ шрифт удалён, ! много шрифтов, null — всё в порядке. */
  onStatus?: (status: FontFieldStatus | null) => void;
  disabled?: boolean;
}

export function FontField(props: FontFieldProps) {
  const { label = "Шрифт", value, theme, registry, autoRole, familiesOnPage, disabled, onChange, onHover, onStatus } = props;
  const shop = useShopFonts();
  const { remember } = useRecentFonts();
  const [picking, setPicking] = useState(false);

  const mode: FontMode = value.kind;
  const state = value.kind === "custom" ? customFontState(value.font, registry, shop) : null;
  // Шрифт, который реально стоит у элемента: удалённый свой — шрифт роли (как на сайте).
  const effectiveKey = elementFontKey(value, theme, { partRole: autoRole, registry });
  const warning = familiesWarning(familiesOnPage);
  const status = fontFieldStatus(state === "deleted", warning);
  useFieldStatus(status, onStatus);

  const isDefault = value.kind === "role" && value.role === "auto";

  return (
    <div
      className="flex min-w-0 flex-col gap-3"
      data-slot="font-field"
      onMouseEnter={onHover ? () => onHover(true) : undefined}
      onMouseLeave={onHover ? () => onHover(false) : undefined}
    >
      <div className="flex min-h-8 min-w-0 items-center justify-between gap-2">
        <Label>{label}</Label>
        {isDefault ? (
          <span className="type-caption text-on-surface-variant">как у сайта</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="min-h-12 can-hover:min-h-8"
            disabled={disabled}
            onClick={() => onChange({ font: DEFAULT_ELEMENT_FONT })}
            aria-label={`${label}: как у сайта`}
          >
            <RotateCcw aria-hidden />
            как у сайта
          </Button>
        )}
      </div>
      <FontModeSwitch
        value={mode}
        label={label}
        disabled={disabled}
        onChange={(next) => (next === "custom" ? setPicking(true) : onChange({ font: DEFAULT_ELEMENT_FONT }))}
      />
      {value.kind === "role" ? (
        <FontRoleChips
          role={value.role}
          autoRole={autoRole}
          theme={theme}
          registry={registry}
          disabled={disabled}
          onPick={(role) => onChange({ font: { kind: "role", role } })}
          onOpenSiteFonts={props.onOpenSiteFonts}
        />
      ) : (
        <FontCustomCard fontKey={value.font} state={state ?? "ok"} registry={registry} disabled={disabled} onChange={() => setPicking(true)} />
      )}
      {value.kind === "custom" && shop.error !== null ? (
        <NoticeStrip
          tone="warning"
          action={
            <Button variant="outline" onClick={() => void shop.reload()}>
              Повторить
            </Button>
          }
        >
          Ваши шрифты не загрузились. Шрифты сайта доступны.
        </NoticeStrip>
      ) : null}
      <FontTypeFields
        fontKey={effectiveKey}
        registry={registry}
        weight={props.weight}
        case={props.case}
        size={props.size}
        tracking={props.tracking}
        inherit={props.inherit}
        sizePresets={props.sizePresets}
        sizeHint={props.sizeHint}
        sizeExtra={props.sizeExtra}
        disabled={disabled}
        onChange={onChange}
        onPreview={props.onPreview}
        onHover={onHover}
      />
      {warning ? <NoticeStrip tone="warning">{warning}</NoticeStrip> : null}
      <FontCatalogDialog
        open={picking}
        title={`${label}: свой шрифт`}
        current={value.kind === "custom" ? value.font : effectiveKey}
        draftFonts={theme.fonts}
        onClose={() => setPicking(false)}
        onSelect={(key) => {
          setPicking(false);
          remember(key);
          if (value.kind !== "custom" || value.font !== key) onChange({ font: { kind: "custom", font: key } });
        }}
        leading={(chosen, choose) => <FontQuickPicks fonts={theme.fonts} registry={registry} chosen={chosen} onChoose={choose} />}
      />
    </div>
  );
}
